import React, { FC, ReactNode, useLayoutEffect, useRef } from 'react';
import classnames from 'classnames/bind';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '@clearcover/atlas';
import { Asset } from 'Shared/components/Asset';
import styles from './BlockListModalShell.module.css';

const cx = classnames.bind(styles);

type AssetVariant = React.ComponentProps<typeof Asset>['variant'];

export type BlockListModalShellProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Synchronous gate evaluated when the user clicks the close X. Return true
   * to BLOCK the close (e.g. during in-flight submit). Returning false or
   * omitting the prop lets close fire normally.
   */
  closeGuard?: () => boolean;
  title: string;
  subtitle?: string;
  icon?: AssetVariant;
  iconTone?: 'danger';
  /** Action button row rendered in Atlas's ModalFooter slot. */
  footer: ReactNode;
  /**
   * Atlas Modal variant. Defaults to 'normal'. Variant only controls Atlas's
   * defaults for max-height / max-width — our `minWidth` override below
   * neutralizes the variant's max-width cap by writing min/max-width
   * imperatively onto Atlas's `.content` element.
   */
  modalVariant?: 'full' | 'normal' | 'tall' | 'xl';
  /**
   * Minimum pixel width for the modal window. Pinned imperatively on Atlas's
   * `.content` element (the white modal envelope) so the modal — not just an
   * inner wrapper — is guaranteed this width. Required when content has
   * intrinsic min-width or when the viewport is too narrow for Atlas's
   * variant max-width to satisfy the content (e.g. variant 'normal' caps
   * at 50vw, which is < 620px on viewports under 1240px).
   */
  minWidth?: number;
  /**
   * Maximum pixel width for the modal window. Pinned imperatively on Atlas's
   * `.content` element to override the variant's viewport-relative cap (50vw
   * on 'normal') so the modal stays at a stable design width across viewport
   * sizes. Pass the same value as `minWidth` to lock the modal to a fixed
   * width.
   */
  maxWidth?: number;
  children: ReactNode;
};

export const BlockListModalShell: FC<BlockListModalShellProps> = ({
  open,
  onClose,
  closeGuard,
  title,
  subtitle,
  icon,
  iconTone,
  footer,
  modalVariant = 'normal',
  minWidth,
  maxWidth,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  /*
   * Atlas Modal does not expose className / style props on its `.content`
   * element (the white modal envelope). Our wrapper sits inside `.content` —
   * setting min-width on the wrapper produces overflow when the variant's
   * max-width (50vw on 'normal') is narrower than the desired min-width.
   *
   * Apply min-width directly to `.content` via the wrapper's parentElement so
   * the modal envelope itself respects the floor. Clearing max-width prevents
   * the variant's cap from clipping the envelope when minWidth exceeds 50vw.
   */
  useLayoutEffect(() => {
    if (!open || (!minWidth && !maxWidth) || !wrapperRef.current) return;
    const atlasContent = wrapperRef.current.parentElement;
    if (!atlasContent) return;
    const prevMinWidth = atlasContent.style.minWidth;
    const prevMaxWidth = atlasContent.style.maxWidth;
    if (minWidth) atlasContent.style.minWidth = `${minWidth}px`;
    atlasContent.style.maxWidth = maxWidth ? `${maxWidth}px` : 'none';
    return () => {
      atlasContent.style.minWidth = prevMinWidth;
      atlasContent.style.maxWidth = prevMaxWidth;
    };
  }, [open, minWidth, maxWidth]);

  const handleClose = () => {
    if (closeGuard?.()) return;
    onClose();
  };

  return (
    <Modal
      open={open}
      controlled
      handleClose={handleClose}
      variant={modalVariant}
    >
      {() => (
        <div ref={wrapperRef} className={styles.widthWrapper}>
          <ModalHeader>
            {/* data-testid preserved for existing AddAttributeModal tests */}
            <div className={styles.header} data-testid="form-modal-header">
              {icon && (
                <div
                  className={cx('iconTile', iconTone && `iconTone_${iconTone}`)}
                >
                  <Asset variant={icon} />
                </div>
              )}
              <div className={styles.titleBlock}>
                <h2 className={styles.title}>{title}</h2>
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              </div>
            </div>
          </ModalHeader>
          <ModalContent>
            <div className={styles.bodyPadding}>{children}</div>
          </ModalContent>
          <ModalFooter>
            <div className={styles.footerRow}>{footer}</div>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
};

BlockListModalShell.displayName = 'BlockListModalShell';
